// Brain Dump Service - API calls for the standalone brain dump endpoint

import axios, { type AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Session token storage (sessionStorage - clears on tab close)
const TOKEN_KEY = 'cm_dump_token';

const getToken = (): string | null => sessionStorage.getItem(TOKEN_KEY);
const setToken = (token: string): void => sessionStorage.setItem(TOKEN_KEY, token);
const clearToken = (): void => sessionStorage.removeItem(TOKEN_KEY);

// Separate axios instance (no JWT interceptors from the main app)
export const dumpApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000, // 2 minutes for transcription
});

// Add auth token to requests
dumpApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally: clear stale token and redirect to login
dumpApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearToken();
      window.location.reload();
    }
    return Promise.reject(error);
  },
);

// Types
export interface DumpUploadResponse {
  submission_id: string;
  status: string;
  message: string;
  content_sources: string[];
  source_file_count: number;
  source_audio_count: number;
  source_text_length: number;
}

export interface DumpSubmissionSummary {
  id: string;
  status: string;
  content_sources: string[] | null;
  source_text_length: number;
  source_audio_count: number;
  source_file_count: number;
  created_at: string;
  preview: string | null;
}

export interface SalespersonOption {
  id: string;
  name: string;
}

export const brainDumpService = {
  /** Check if we have a valid session */
  isAuthenticated: (): boolean => !!getToken(),

  /** Authenticate with the brain dump password */
  authenticate: async (password: string): Promise<boolean> => {
    try {
      const response = await dumpApi.post<{ token: string; expires_in: number }>(
        '/dump/auth',
        { password },
      );
      setToken(response.data.token);
      return true;
    } catch {
      return false;
    }
  },

  /** Clear the session */
  logout: (): void => {
    clearToken();
  },

  /** Upload content (files, text, audio) with optional salesperson */
  upload: async (
    files: File[],
    textContent: string | null,
    audioFiles: File[],
    salespersonId: string | null = null,
  ): Promise<DumpUploadResponse> => {
    const formData = new FormData();

    files.forEach((file) => formData.append('files', file));
    if (textContent?.trim()) {
      formData.append('text_content', textContent.trim());
    }
    audioFiles.forEach((file) => formData.append('audio_files', file));
    if (salespersonId) {
      formData.append('salesperson_id', salespersonId);
    }

    const response = await dumpApi.post<DumpUploadResponse>('/dump/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });

    return response.data;
  },

  /** List recent submissions */
  listSubmissions: async (limit: number = 10): Promise<DumpSubmissionSummary[]> => {
    const response = await dumpApi.get<DumpSubmissionSummary[]>(
      `/dump/submissions?limit=${limit}`,
    );
    return response.data;
  },

  /** Fetch available salespeople for the dropdown */
  getSalespeople: async (): Promise<SalespersonOption[]> => {
    const response = await dumpApi.get<SalespersonOption[]>('/dump/salespeople');
    return response.data;
  },
};
